import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { consent } from '../services/consent';
import { useT } from '../services/i18n';

// Bottom cookie banner (analytics only): Accept all / Refuse / Customise. Re-openable via footer « Manage cookies ».
const CookieBanner = () => {
  const { t } = useT();
  const [open, setOpen] = useState(() => !consent.decided());
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(() => consent.analytics());
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    consent.init();
    const show = () => { setAnalytics(consent.analytics()); setCustom(true); setOpen(true); };
    const onDrawer = (e) => setHidden(!!e.detail?.open);
    window.addEventListener('cookie-consent-open', show);
    window.addEventListener('cart-drawer-state', onDrawer);
    return () => { window.removeEventListener('cookie-consent-open', show); window.removeEventListener('cart-drawer-state', onDrawer); };
  }, []);

  if (!open) return null;
  const decide = (value) => { consent.set(value); setOpen(false); setCustom(false); };

  return (
    <div className={`fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 transition-transform duration-300 ease-in-out ${hidden ? 'translate-y-full pointer-events-none' : 'translate-y-0'}`} role="dialog" aria-live="polite" aria-label={t('cookies.title')} data-testid="cookie-banner" data-hidden={hidden ? '1' : '0'}>
      <div className="mx-auto max-w-4xl rounded-2xl border border-gray-700 bg-gray-900/95 text-gray-100 shadow-2xl backdrop-blur px-4 py-4 sm:px-6">
        <div className="flex items-start gap-3">
          <Cookie size={22} className="text-orange-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm sm:text-base">{t('cookies.title')}</p>
            <p className="text-xs sm:text-sm text-gray-300 mt-1">{t('cookies.text')}</p>
            {custom && (
              <div className="mt-3 space-y-2 text-xs sm:text-sm" data-testid="cookie-customise">
                <label className="flex items-start gap-2 rounded-lg bg-gray-800 px-3 py-2">
                  <input type="checkbox" checked disabled className="mt-0.5 accent-orange-500" data-testid="cookie-necessary" />
                  <span><span className="font-medium">{t('cookies.necessary')}</span> <span className="text-gray-400">— {t('cookies.necessaryHint')}</span></span>
                </label>
                <label className="flex items-start gap-2 rounded-lg bg-gray-800 px-3 py-2 cursor-pointer">
                  <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-0.5 accent-orange-500" data-testid="cookie-analytics" />
                  <span><span className="font-medium">{t('cookies.analytics')}</span> <span className="text-gray-400">— {t('cookies.analyticsHint')}</span></span>
                </label>
              </div>
            )}
          </div>
          {consent.decided() && <button type="button" onClick={() => setOpen(false)} aria-label={t('cookies.close')} className="text-gray-400 hover:text-white" data-testid="cookie-close"><X size={18} /></button>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 sm:justify-end">
          {custom ? (
            <button type="button" onClick={() => decide(analytics)} className="rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-semibold text-white" data-testid="cookie-save">{t('cookies.save')}</button>
          ) : (
            <button type="button" onClick={() => setCustom(true)} className="rounded-full border border-gray-600 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800" data-testid="cookie-customise-btn">{t('cookies.customise')}</button>
          )}
          <button type="button" onClick={() => decide(false)} className="rounded-full border border-gray-600 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800" data-testid="cookie-refuse">{t('cookies.refuse')}</button>
          <button type="button" onClick={() => decide(true)} className="rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-semibold text-white" data-testid="cookie-accept">{t('cookies.accept')}</button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
